import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { applyCampaign, applyCampaignMCQ, getSampleApplicationStatus } from "@/api"
import { type EAffiliateCampaignType, ECampaignType, EValidateUser } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { sendOpenLink } from "@/modules/helpers/utils"

const POLLING_MAX_ATTEMPTS = 6
const POLLING_INTERVAL_MS = 10000

interface IUseApplyCampaignProps {
  campaignId: string
  type: ECampaignType
  link?: string
  onCompleted?: (params?: { isRequestRegisterSample?: boolean; kolSubmissionId?: string }) => void
  onMutationSuccess?: (kolSubmissionId?: string) => void
}

export const useApplyCampaign = ({
  campaignId,
  type,
  link,
  onCompleted,
  onMutationSuccess
}: IUseApplyCampaignProps) => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data?: {
      phoneNumber: string
      fullName: string
      address: string
      productId?: string
      infoApplyKOC?: {
        contentTypes: EAffiliateCampaignType[]
        skuId?: string
        totalClip: string
      }
    }) => {
      return applyCampaign(String(campaignId), type, data)
    },
    mutationKey: ["apply_campaign_" + type],
    async onSuccess(data) {
      await queryClient.resetQueries({
        queryKey: [`manage`, null]
      })

      if (type?.toLocaleUpperCase() === ECampaignType.AFFILIATE && data?.data?.data) {
        useCurrentUser.setState({
          user: data?.data?.data
        })

        const kolSubmissionId = data?.data?.data?.kolSubmissionId
        if (kolSubmissionId) {
          if (link) {
            sendOpenLink(link)
          }
          onMutationSuccess?.(kolSubmissionId)

          try {
            let isRequestRegisterSample = false

            for (let attempt = 0; attempt < POLLING_MAX_ATTEMPTS; attempt++) {
              const res = await getSampleApplicationStatus(kolSubmissionId)
              if (res?.data?.data?.isRequestRegisterSample) {
                isRequestRegisterSample = true
                break
              }
              // Wait before next attempt (skip wait on last attempt)
              if (attempt < POLLING_MAX_ATTEMPTS - 1) {
                await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS))
              }
            }

            onCompleted?.({ isRequestRegisterSample, kolSubmissionId })
          } catch (error) {
            console.error("Failed to get sample application status:", error)
            onCompleted?.({ isRequestRegisterSample: false, kolSubmissionId })
          }
          return
        }
      }

      onCompleted?.({ isRequestRegisterSample: false })
    },
    onError(error: any) {
      const type = error.response?.data?.error?.type
      if (type?.includes(EValidateUser.KOL_SUBMISSION_ALREADY_APPLIED)) {
        toast.error("Bạn đã đăng ký chiến dịch này", {
          richColors: true,
          className: "bg-red-500 text-white"
        })
        return
      }
      toast.error(error.response?.data?.message, {
        richColors: true,
        className: "bg-red-500 text-white"
      })
    }
  })

  return {
    ...mutation,
    isPending: mutation.isPending
  }
}

export const useApplyCampaignMCQ = ({ campaignId, type, onCompleted }: IUseApplyCampaignProps) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data?: {
      phoneNumber: string
      fullName: string
      address: string
      productId?: string
      infoApplyKOC?: {
        skus?: { skuId: string; productId: string }[]
        contentTypes: EAffiliateCampaignType[]
        totalClip: string
      }
    }) => {
      return applyCampaignMCQ(String(campaignId), type, data)
    },
    mutationKey: ["apply_campaign_mcq_" + type],
    async onSuccess(data) {
      await queryClient.resetQueries({
        queryKey: [`manage`, null]
      })
      if (type?.toLocaleUpperCase() === ECampaignType.AFFILIATE && data?.data?.data) {
        useCurrentUser.setState({
          user: data?.data?.data
        })
      }
      onCompleted?.()
    },
    onError(error: any) {
      const type = error.response?.data?.error?.type
      if (type?.includes(EValidateUser.KOL_SUBMISSION_ALREADY_APPLIED)) {
        toast.error("Bạn đã đăng ký chiến dịch này", {
          richColors: true,
          className: "bg-red-500 text-white"
        })
        return
      }
      toast.error(error.response?.data?.message, {
        richColors: true,
        className: "bg-red-500 text-white"
      })
    }
  })
}
